/**
 * <site-header>: the navigation bar morphs into a floating pill once the page scrolls, and opens the mobile menu.
 * The morph animates transforms only: this element measures how far the brand and the links must travel to sit
 * inside the pill and hands the distances to the stylesheet as --shift-l and --shift-r.
 */

// Dependencies
import { registerComponent } from 'almostnojs';
import { Enhanced } from './enhanced.js';

// Scroll distance before the bar becomes a pill.
const PILL_AT = 16;

// The pill's inner padding, left and right (phone values below 720px), matching the stylesheet.
const PAD = { wide: [22, 10], phone: [16, 8] };

/**
 * The site header.
 */
class SiteHeader extends Enhanced {

    /**
     * Wire the scroll morph and the mobile menu.
     *
     * @returns {void}
     */
    init() {

        // Parts.
        this.header = this.querySelector('.site-header');
        this.button = this.querySelector('.menu-btn');
        this.menu = this.querySelector('.mobile-nav');
        if (!this.header) return;

        // Where the brand and the links go when the bar becomes a pill; re-measured whenever the layout changes.
        this.inner = this.querySelector('.header-inner');
        this.phone = window.matchMedia('(max-width: 720px)');
        this.measure();
        const remeasure = () => this.measure();
        const observer = new ResizeObserver(remeasure);
        observer.observe(this.inner);
        this.own(() => observer.disconnect());
        if (document.fonts?.ready) document.fonts.ready.then(remeasure);

        // Bar or pill.
        const onScroll = () => this.header.classList.toggle('scrolled', window.scrollY > PILL_AT);
        onScroll();
        this.listen(window, 'scroll', onScroll, { passive: true });

        // Mobile menu.
        if (!this.button || !this.menu) return;
        this.listen(this.button, 'click', () => this.setOpen(!this.header.classList.contains('menu-open')));
        this.listen(document, 'keydown', (e) => {
            if (e.key === 'Escape' && this.header.classList.contains('menu-open')) { this.setOpen(false); this.button.focus(); }
        });
        this.listen(window.matchMedia('(min-width: 901px)'), 'change', (e) => { if (e.matches) this.setOpen(false); });
    }

    /**
     * Measure the travel from the bar's layout to the pill's padding and publish it to the stylesheet.
     *
     * @returns {void}
     */
    measure() {

        // Parts (offsets ignore transforms, so these are the bar positions whatever the current state).
        const brand = this.inner?.querySelector('.brand'), right = this.inner?.querySelector('.header-right');
        if (!brand || !right) return;

        // The pill's box inside the bar.
        const width = this.inner.clientWidth, pill = Number.parseFloat(getComputedStyle(this.inner, '::before').width) || width;
        const left = (width - pill) / 2, [padL, padR] = this.phone.matches ? PAD.phone : PAD.wide;

        // Travel for each side.
        const shiftL = left + padL - brand.offsetLeft;
        const shiftR = left + pill - padR - (right.offsetLeft + right.offsetWidth);
        this.header.style.setProperty('--shift-l', `${Math.round(shiftL)}px`);
        this.header.style.setProperty('--shift-r', `${Math.round(shiftR)}px`);
    }

    /**
     * Open or close the mobile menu.
     *
     * @param {boolean} open - Open.
     * @returns {void}
     */
    setOpen(open) {

        // State on the header and the button.
        this.header.classList.toggle('menu-open', open);
        this.button.setAttribute('aria-expanded', String(open));
        this.button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');

        // The page behind neither scrolls nor takes focus while the menu is open.
        document.documentElement.classList.toggle('no-scroll', open);
        for (const region of document.querySelectorAll('main, .site-footer, .skip')) region.inert = open;

        // Focus moves into the menu when it opens.
        if (open) this.menu.querySelector('a')?.focus();
    }
}

// Register
registerComponent('site-header', SiteHeader);
